import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Close,
  ArrowUpward,
  CheckCircle,
  OpenInNew,
  Publish
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../App';
import {
  STATUS_DISPLAY_MAP,
  APP_STATUS_DEPLOYED,
  APP_STATUS_ERROR,
  APP_STATUS_CONFIRMING_DEMAND,
  isCompletedStatus,
  isFailedStatus
} from '../constants/status';

const ProjectPage = () => {
  const { id } = useParams(); // undefined if on /chat route
  const navigate = useNavigate();
  const location = useLocation();
  const [appId, setAppId] = useState(id); // Track app_id, can be set later
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(id ? true : false); // Only load if we have an id
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [statusMessages, setStatusMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (id) {
      loadApp();
    } else {
      // New project - check for initialMessage from idea page
      const initialMessage = location.state?.initialMessage;
      if (initialMessage) {
        // Fill input box with initial message, let user review and send
        setUserInput(initialMessage);
      }
    }
    return () => {
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [id]);

  useEffect(() => {
    // Only scroll to bottom if there are messages
    if (messages.length > 0 || statusMessages.length > 0) {
      scrollToBottom();
    }
  }, [messages, statusMessages]);

  const scrollToBottom = () => {
    // Use a small delay to ensure content is rendered
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const loadApp = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      const { data: appData, error } = await supabase
        .from('apps')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setApp(appData);

      // Load chat history from messages table
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .eq('app_id', id)
        .order('created_at', { ascending: true });

      let loadedMessages = [];
      if (messagesError) {
        console.error('Error loading messages:', messagesError);
      } else if (messagesData && messagesData.length > 0) {
        // Convert database messages to UI format
        loadedMessages = messagesData.map(msg => ({
          role: msg.role,
          content: msg.content
        }));
        setMessages(loadedMessages);
        console.log('Loaded messages from database:', loadedMessages.length);
      }

      // If app has code_url, construct the app URL and show preview
      if (appData.code_url && appData.project_name) {
        const appUrl = `https://www.xpainkiller.com/apps/${appData.project_name}`;
        setGeneratedUrl(appUrl);
        setIsReady(true);
      }

      setLoading(false);

      const initialMessage = location.state?.initialMessage || appData.user_requirement;
      if (initialMessage && appData.status === 'pending' && loadedMessages.length === 0) {
        await handleSendMessage(initialMessage, false);
      }

      // Start polling for status updates (unless already deployed)
      if (!isCompletedStatus(appData.status)) {
        startPolling();
      }
    } catch (error) {
      console.error('Error loading app:', error);
      setLoading(false);
    }
  };

  const startPolling = (targetAppId = null) => {
    const pollId = targetAppId || appId || id;
    if (!pollId) {
      console.warn('startPolling called without valid appId');
      return;
    }

    // Clean up existing interval if any
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Polling function - extracted so we can call it immediately
    const pollOnce = async () => {
      try {
        // Poll for app status
        const { data: appData, error } = await supabase
          .from('apps')
          .select('*')
          .eq('id', pollId)
          .single();

        if (error) throw error;

        setApp(appData);

        // Also poll for new messages - sync with database
        const { data: messagesData, error: messagesError } = await supabase
          .from('messages')
          .select('*')
          .eq('app_id', pollId)
          .order('created_at', { ascending: true });

        if (!messagesError && messagesData) {
          const dbMessages = messagesData.map(msg => ({
            role: msg.role,
            content: msg.content
          }));
          console.log('Polling - dbMessages:', dbMessages.length, 'messages', dbMessages);
          // Only update if database has more messages (new assistant responses)
          // This prevents flickering when user's local message hasn't been saved yet
          setMessages(prev => {
            console.log('Polling - prev.length:', prev.length, 'dbMessages.length:', dbMessages.length);
            if (dbMessages.length >= prev.length) {
              console.log('Polling - updating messages');
              return dbMessages;
            }
            console.log('Polling - NOT updating messages');
            return prev;
          });
        } else {
          console.log('Polling - messagesError or no data:', messagesError);
        }

        // Map backend status to user-friendly messages using constants
        const statusInfo = STATUS_DISPLAY_MAP[appData.status];
        if (statusInfo) {
          // Replace all status messages with current status
          setStatusMessages([{
            text: statusInfo.text,
            status: statusInfo.status,
            timestamp: Date.now()
          }]);
        }

        // If deployed and has code_url, construct app URL and show preview
        if (isCompletedStatus(appData.status) && appData.code_url && appData.project_name) {
          const appUrl = `https://www.xpainkiller.com/apps/${appData.project_name}`;
          setGeneratedUrl(appUrl);
          setIsReady(true);

          // Stop polling once deployed
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
        } else if (isFailedStatus(appData.status)) {
          // Stop polling on failure
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
        }
      } catch (error) {
        console.error('Error polling app status:', error);
      }
    };

    // Execute immediately, then continue every 2 seconds
    pollOnce();
    pollIntervalRef.current = setInterval(pollOnce, 2000);
  };

  const handleSendMessage = async (message = userInput, skipAddMessage = false) => {
    if (!message.trim() || isGenerating) return;

    const messageToSend = message.trim();

    setUserInput('');
    setIsGenerating(true);
    setStatusMessages([]);
    // Hide "Your app is ready!" card immediately when sending new message
    setGeneratedUrl(null);
    setIsReady(false);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/');
        return;
      }

      let currentAppId = appId;

      if (!currentAppId) {
        const createResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/apps/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (!createResponse.ok) {
          throw new Error('Failed to create app');
        }

        const createData = await createResponse.json();
        currentAppId = createData.app_id;
        setAppId(currentAppId);
        navigate(`/project/${currentAppId}`, { replace: true });
        startPolling(currentAppId);
      }

      await supabase.from('messages').insert({
        app_id: currentAppId,
        role: 'user',
        content: messageToSend
      });

      if (!skipAddMessage) {
        setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
      }

      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/chat?app_id=${currentAppId}`;
      console.log("Calling api", apiUrl)
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ message: messageToSend })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Chat API error:', response.status, errorText);
        throw new Error(`Chat request failed: ${errorText}`);
      }

      // API returned successfully - polling will sync messages from database
      // No need to manually add message here since:
      // 1. Backend saves assistant message to database
      // 2. Polling fetches messages from database every 2 seconds
      await response.json();

      // Restart polling to track status changes (polling also syncs messages)
      startPolling(currentAppId);

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.'
      }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    addStatusMessage('Tweaking code', 'processing');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/apps/${id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Generation failed');
      }

      const data = await response.json();

      addStatusMessage('Code updated', 'completed');
      addStatusMessage('Todo list updated', 'completed');
      addStatusMessage('Command completed', 'completed');

      setGeneratedUrl(data.url);

      // Add success message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Your app is ready! You can view it or make changes below.`
      }]);

    } catch (error) {
      console.error('Error generating app:', error);
      alert('Generation failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const addStatusMessage = (text, status) => {
    setStatusMessages([{ text, status, timestamp: Date.now() }]);
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }


  // Chat/Generation view
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        background: 'rgba(15, 15, 15, 0.9)',
        backdropFilter: 'blur(10px)',
        zIndex: 10
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          maxWidth: '900px',
          mx: 'auto',
          width: '100%'
        }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
          <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
            {app?.app_name || 'New App'}
          </Typography>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '900px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Messages */}
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            <Box sx={{
              maxWidth: '80%',
              p: 2,
              borderRadius: 3,
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                : 'rgba(255,255,255,0.05)',
              border: msg.role === 'assistant' ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }}>
              <Typography sx={{
                color: msg.role === 'user' ? 'white' : '#e5e7eb',
                whiteSpace: 'pre-wrap'
              }}>
                {msg.content}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Status messages */}
        {statusMessages.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            {statusMessages.map((status, idx) => (
              <Box key={idx} sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                {status.status === 'completed' ? (
                  <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                ) : (
                  <CircularProgress size={16} sx={{ color: '#6366f1' }} />
                )}
                <Typography sx={{ fontSize: '14px', color: '#d1d5db' }}>
                  {status.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* App Ready Card */}
        {generatedUrl && app?.status === APP_STATUS_DEPLOYED && (
          <Box sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.03)',
            mt: 2,
            display: 'flex',
            gap: 3,
            alignItems: 'center'
          }}>
            {/* Video Preview */}
            {app?.thumbnail_url && app.thumbnail_url.endsWith('.webm') && (
              <Box sx={{
                width: 120,
                height: 200,
                borderRadius: 2,
                overflow: 'hidden',
                flexShrink: 0,
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <video
                  src={app.thumbnail_url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onLoadedMetadata={(e) => { e.target.playbackRate = 4.0; }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            )}

            {/* QR Code + Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', height: 200 }}>
              <Box sx={{
                p: 1.5,
                background: 'white',
                borderRadius: 2,
                flexShrink: 0
              }}>
                <QRCodeSVG
                  value={generatedUrl}
                  size={100}
                  level="M"
                />
              </Box>

              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                  onClick={() => window.open(generatedUrl, '_blank')}
                  sx={{
                    flex: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    }
                  }}
                >
                  Open
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Publish sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    try {
                      const { error } = await supabase.from('apps').update({ is_public: true }).eq('id', app.id);
                      if (error) {
                        console.error('Error publishing:', error);
                        return;
                      }
                      // Navigate to home page first, then scroll to apps section
                      navigate('/');
                      setTimeout(() => {
                        const appsSection = document.getElementById('apps-section');
                        if (appsSection) {
                          appsSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 500);
                    } catch (error) {
                      console.error('Error publishing:', error);
                    }
                  }}
                  sx={{
                    flex: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.4)',
                      background: 'rgba(255,255,255,0.05)'
                    }
                  }}
                >
                  Publish
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Bottom Input */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(15, 15, 15, 0.95)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        bottom: 0,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)'
      }}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          maxWidth: '900px',
          mx: 'auto'
        }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
            placeholder={app?.status === APP_STATUS_DEPLOYED ? "You can continue editing this app..." : "Describe what you want to build..."}
            disabled={isGenerating || (app?.status && app.status !== APP_STATUS_CONFIRMING_DEMAND && app.status !== APP_STATUS_DEPLOYED)}
            variant="standard"
            InputProps={{
              disableUnderline: true
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '15px',
                color: 'white',
                '&::placeholder': {
                  color: '#6b7280',
                  opacity: 1
                }
              },
              '& .MuiInputBase-root': {
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 3,
                px: 2,
                py: 1.5
              }
            }}
          />

          <IconButton
            onClick={() => handleSendMessage()}
            disabled={!userInput.trim() || isGenerating || (app?.status && app.status !== APP_STATUS_CONFIRMING_DEMAND && app.status !== APP_STATUS_DEPLOYED)}
            sx={{
              background: userInput.trim() && !isGenerating && (!app?.status || app.status === APP_STATUS_CONFIRMING_DEMAND || app.status === APP_STATUS_DEPLOYED)
                ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                : 'rgba(255,255,255,0.1)',
              color: 'white',
              width: 44,
              height: 44,
              '&:hover': {
                background: userInput.trim() && !isGenerating && (!app?.status || app.status === APP_STATUS_CONFIRMING_DEMAND || app.status === APP_STATUS_DEPLOYED)
                  ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                  : 'rgba(255,255,255,0.1)',
              },
              '&.Mui-disabled': {
                background: 'rgba(255,255,255,0.05)',
                color: '#6b7280'
              }
            }}
          >
            {app?.status && app.status !== APP_STATUS_CONFIRMING_DEMAND && app.status !== APP_STATUS_DEPLOYED ? (
              <CircularProgress size={20} sx={{ color: '#6366f1' }} />
            ) : (
              <ArrowUpward />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectPage;
